import { createElement, useCallback, useState, type ReactElement } from "react";
import { Code2 } from "lucide-react";
import { StorySqlDialog, type StorySqlChart, type StorySqlTextItem } from "../components/organisms/StorySqlDialog";
import { pickSqlChartId } from "../components/organisms/StoryCommandHeader/storyCommandHeaderMappers";
import type { MenuItemConfig } from "../components/registry/configRegistry.types";
import { useRegisterCommands } from "../contexts/CommandRegistryContext";
import { useIsViewerMode } from "../contexts/ViewerModeContext";
import { useStoryPath } from "../contexts/StoryPathContext";
import { HIDDEN, READY, entityArg, soonInViewer } from "../services/commands/actionHelpers";
import { getFocusedTileId } from "../services/commands/focusedTile";

export const VIEW_SQL_MENU_ID = "story-view-sql";
const MENU_LABEL = "View SQL";
const COMMAND_LABEL = "View SQL for this story";

interface Args {
  storyId: string;
  storyTitle: string;
  /** The charts View SQL can show (new path, editable). */
  sqlCharts: StorySqlChart[];
  /** The chart open in the editor, if any: View SQL starts there. */
  openEditorItemId?: string | null;
  /** Text tiles, shown greyed as "No query" rows. */
  textItems?: StorySqlTextItem[];
  /**
   * The story is on the new path, as the page published it. The header that mounts this hook sits outside the
   * page's StoryPathProvider, where the context always reads "old"; absent, the context decides.
   */
  isNewPath?: boolean;
}

/**
 * View SQL for the story ⋯ menu: a `MenuItemConfig` for the menu, the dialog element to mount,
 * and the ⌘K `story.viewSql` command (the shortcut opens the focused chart's SQL). New path only.
 */
export const useStoryViewSqlAction = ({
  storyId,
  storyTitle,
  sqlCharts,
  openEditorItemId,
  textItems,
  isNewPath: publishedNewPath,
}: Args): { menuItem: MenuItemConfig; open: () => void; dialog: ReactElement } => {
  const isViewer = useIsViewerMode();
  const contextNewPath = useStoryPath() === "new";
  const isNewPath = publishedNewPath ?? contextNewPath;
  const [isOpen, setIsOpen] = useState(false);
  const [pickedChartId, setPickedChartId] = useState<string | null>(null);

  const pickedStillListed = sqlCharts.some((option) => option.id === pickedChartId);
  const initialChartId = pickedStillListed ? pickedChartId : pickSqlChartId(sqlCharts, openEditorItemId);

  const open = useCallback(() => {
    setPickedChartId(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  useRegisterCommands(soonInViewer([
    {
      id: "story.viewSql", label: COMMAND_LABEL, group: "story", keywords: ["query", "code", "sql"],
      patterns: ["view sql", "show sql", "view sql for {chart}", "show sql for {chart}", "view the sql for {chart}", "show the sql for {chart}"],
      shortcut: { chords: [{ key: "s", mod: true, shift: true }] },
      args: [entityArg("chart", "chart", "The chart whose query to show", () => sqlCharts.map((c) => ({ id: c.id, label: c.label })), false)],
      isAvailable: () => (isNewPath && sqlCharts.length > 0 ? READY : HIDDEN),
      run: (args, ctx) => {
        const wanted = args.chart ?? (ctx.source === "keyboard" ? getFocusedTileId() : null);
        const picked = sqlCharts.find((option) => option.id === wanted);
        setPickedChartId(picked?.id ?? null);
        setIsOpen(true);
        return { status: "done", message: picked ? `Showing the SQL for ${picked.label}.` : "Showing the SQL." };
      },
    },
  ], isViewer));

  const menuItem: MenuItemConfig = {
    id: VIEW_SQL_MENU_ID,
    label: MENU_LABEL,
    icon: Code2,
    disabled: !isNewPath || sqlCharts.length === 0,
  };
  const dialog = createElement(StorySqlDialog, {
    open: isOpen && isNewPath,
    storyId,
    storyTitle,
    charts: sqlCharts,
    textItems,
    initialChartId,
    onClose: close,
  });
  return { menuItem, open, dialog };
};
